/* eslint-disable react/no-unknown-property */
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Center } from "@react-three/drei";
import { SVGLoader } from "three-stdlib";
import ImageTracer from "imagetracerjs";
import { IMAGES } from "../../data";

// Trace the original War Room Performance logo raster into real vector paths
// and extrude them into true 3D geometry. Two color groups so the pink
// sparkles keep their brand color while the fighter emblem + wordmark are a
// premium metallic white.
function useTracedLogoGroups() {
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const src = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const trace = (predicate) => {
        const idata = ctx.createImageData(canvas.width, canvas.height);
        for (let i = 0; i < src.data.length; i += 4) {
          const r = src.data[i];
          const g = src.data[i + 1];
          const b = src.data[i + 2];
          const on = predicate(r, g, b);
          idata.data[i] = on ? 255 : 0;
          idata.data[i + 1] = on ? 255 : 0;
          idata.data[i + 2] = on ? 255 : 0;
          idata.data[i + 3] = 255;
        }
        const svgStr = ImageTracer.imagedataToSVG(idata, {
          numberofcolors: 2,
          colorsampling: 0,
          colorquantcycles: 1,
          pathomit: 8,
          blurradius: 0,
          ltres: 1.2,
          qtres: 1.2,
          strokewidth: 0,
          linefilter: true,
          roundcoords: 1,
          pal: [
            { r: 0, g: 0, b: 0, a: 255 },
            { r: 255, g: 255, b: 255, a: 255 },
          ],
        });
        const parsed = new SVGLoader().parse(svgStr);
        const shapes = [];
        for (const p of parsed.paths) {
          if (p.color.getHex() !== 0xffffff) continue;
          for (const s of SVGLoader.createShapes(p)) shapes.push(s);
        }
        return shapes;
      };

      const pinkShapes = trace((r, g, b) => r > 170 && g < 130 && b > 90);
      const whiteShapes = trace(
        (r, g, b) => r > 170 && g > 170 && b > 170
      );

      if (!cancelled) {
        setGroups([
          {
            shapes: whiteShapes,
            material: {
              color: "#EDEDED",
              metalness: 0.72,
              roughness: 0.28,
            },
          },
          {
            shapes: pinkShapes,
            material: {
              color: "#FF1493",
              metalness: 0.5,
              roughness: 0.3,
              emissive: "#FF1493",
              emissiveIntensity: 0.18,
            },
          },
        ]);
      }
    };
    img.onerror = () => setGroups([]);
    img.src = IMAGES.logoRaster || IMAGES.logo;
    return () => {
      cancelled = true;
    };
  }, []);

  return groups;
}

const EXTRUDE = {
  depth: 14,
  bevelEnabled: true,
  bevelSize: 1.4,
  bevelThickness: 1.4,
  bevelSegments: 3,
  curveSegments: 8,
};

function Emblem() {
  const groups = useTracedLogoGroups();
  const spinner = useRef(null);

  useFrame((_, delta) => {
    if (spinner.current) spinner.current.rotation.y += delta * 0.28;
  });

  const meshes = useMemo(() => {
    if (!groups) return null;
    return groups.flatMap((g, gi) =>
      g.shapes.map((shape, si) => (
        <mesh key={`${gi}-${si}`} castShadow receiveShadow>
          <extrudeGeometry args={[shape, EXTRUDE]} />
          <meshStandardMaterial {...g.material} />
        </mesh>
      ))
    );
  }, [groups]);

  if (!meshes) return null;

  return (
    <group ref={spinner}>
      {/* Center normalises the bbox; the inner scale.y = -1 flips SVG y-down
          into three's y-up. Outer scale sets emblem size in the hero. */}
      <Center>
        <group scale={[1, -1, 1]}>{meshes}</group>
      </Center>
    </group>
  );
}

export default function Scene3D() {
  return (
    <Canvas
      camera={{ position: [0, 0, 6.5], fov: 42 }}
      dpr={[1, 1.75]}
      gl={{ antialias: true, alpha: true }}
      shadows
      style={{ pointerEvents: "none" }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={0.55} />
        <directionalLight position={[3, 4, 5]} intensity={1.4} color="#ffffff" />
        <pointLight position={[6, 3, 4]} intensity={70} color="#FF1493" />
        <pointLight position={[-6, -2, 3]} intensity={55} color="#00E5FF" />
        <pointLight position={[0, 4, -3]} intensity={30} color="#ffffff" />
        {/* Overall size + subtle motion — restrained so the emblem does not
            invade the hero copy area. */}
        <group scale={0.005}>
          <Float speed={0.9} rotationIntensity={0.12} floatIntensity={0.45}>
            <Emblem />
          </Float>
        </group>
      </Suspense>
    </Canvas>
  );
}
